import { AppContext } from '@layout/AppContext'
import { EventNotifications } from '@layout/EventNotifications'
import Header from '@layout/Header'
import Notifications from '@layout/Notifications'
import SideBarManager from '@layout/SideBarManager'
import { PrimeReactProvider } from 'primereact/api'
import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router'

/**
 * PrimeReact configuration
 */
const primeReactConfig = {
  ripple: false
}

/**
 * Main content component wrapper.
 *
 * @component
 * @returns {JSX.Element}
 */
export default function MainContent() {
  const { pathname } = useLocation()
  const [eventNotifications, setEventNotifications] = useState([])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  document.addEventListener(
    'invalid',
    (() => {
      return e => {
        e.preventDefault()
        document.querySelector('input:invalid,textarea:invalid,select:invalid').focus()
      }
    })(),
    true
  )

  /**
   * Add notification to notification manager.
   *
   * @param {object} newNotification - Notification object
   * @param {string} newNotification.type - Notification type
   * @param {string} newNotification.notificationId - Notification ID
   * @returns {void}
   */
  function addNotifications(newNotification) {
    setEventNotifications(eventNotifications => {
      // check if the notification of a type with the same id already exists
      if (
        eventNotifications.findIndex(
          notification =>
            notification.type === newNotification.type &&
            notification.notificationId === newNotification.notificationId
        ) === -1
      ) {
        return [...eventNotifications, newNotification]
      }
      return eventNotifications
    })
  }

  /**
   * Remove a specific notification.
   *
   * @param {object} notificationToDelete - Notification to remove
   * @param {string} notificationToDelete.notificationId - Notification ID
   * @param {string} notificationToDelete.type - Notification type
   * @returns {void}
   */
  function removeNotification(notificationToDelete) {
    setEventNotifications(eventNotifications => {
      const index = eventNotifications.findIndex(
        notification =>
          notification.notificationId === notificationToDelete.notificationId &&
          notification.type === notificationToDelete.type
      )
      if (index !== -1) {
        const notifications = [...eventNotifications]
        notifications.splice(index, 1)
        return notifications
      }
      return eventNotifications
    })
  }

  /**
   * Remove all notifications of a given type or types.
   *
   * @param {string|string[]} type - Notification type or array of types
   * @returns {void}
   */
  function removeNotificationByType(type) {
    setEventNotifications(eventNotifications => {
      if (Array.isArray(type)) {
        const notifications = eventNotifications.filter(
          notification => !type.includes(notification.type)
        )
        return notifications
      } else {
        const notifications = eventNotifications.filter(notification => notification.type !== type)
        return notifications
      }
    })
  }

  return (
    <PrimeReactProvider params={primeReactConfig}>
      <AppContext.Provider
        value={{ addNotifications, removeNotification, removeNotificationByType }}
      >
        <Notifications />
        <Header />
        <SideBarManager />
        <main className="main-content">
          <EventNotifications notifications={eventNotifications} />
          <Outlet />
        </main>
      </AppContext.Provider>
    </PrimeReactProvider>
  )
}
